import {AxiosPromise} from 'axios';
import {createAction} from 'redux-act';

import {serializeToFormData} from 'libs/utils/utils';
import {ENDPOINTS} from 'constants/endpoints';
import {HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'constants/api';
import flags from 'constants/flags/flags';
import {instanceGusaBackend} from 'libs/api';

import {ProductInfoType} from '../../reducers/collection/collectionType';

/**
 * Get faces images from backend
 * @param {string} gender - Current gender
 * @return {AxiosPromise} Faces images from backend
 */
export const fetchSliderFaces = createAction(
    'FETCH_SLIDER_FACES',
    (gender: string): AxiosPromise =>
        instanceGusaBackend.get(ENDPOINTS.TRYON_GET_IMAGES, {
            params: {gender, 'collection-id': Number(flags.newTryOnPhotos.isEnabled())},
        }),
);

/**
 * Update info about current face
 * @param {string} imageId - Face Identification
 * @param {string} productId - Active product identification
 * @return {AxiosPromise} Axios promise
 */
export const updateCurrentFace = createAction<AxiosPromise>(
    'UPDATE_CURRENT_FACE',
    (imageId: string, productId: string) => {
        return instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.TRYON_SET_CURRENT_IMAGE,
            data: serializeToFormData({
                id: imageId,
                product_id: productId,
            }),
        });
    },
    (imageId: string): {imageId: string} => ({
        imageId,
    }),
);

/**
 * Set current face of faces images
 * @param {string} imageId - New identification of faces list
 * @return {number} - imageId data
 */
export const setCurrentFace = createAction<number>('SET_CURRENT_FACE', (imageId: string) =>
    Number(imageId),
);

/**
 * Set current face of faces images
 * @param {number} imageIndex - New identification of faces list
 * @return {number} imageIndex - Action data
 */
export const setActiveFace = createAction<number>(
    'SET_ACTIVE_FACE',
    (imageIndex: number) => imageIndex,
);

/**
 * Set step of "Try on" popup
 * @param {string} currentStep - Which choose step when popup open
 * @return {{currentStep: string}} - Action data
 */
export const setTryOnStep = createAction<{currentStep: string}>(
    'SET_TRY_ON_STEP',
    (currentStep: string) => ({
        currentStep,
    }),
);

/**
 * Handler opening "Try on" popup
 * @param {string} id - Product identification
 * @param {string} name - Product name
 * @param {string} link - Link to product page
 * @return {{id: string, name: string, link: string}} - Action data
 */
export const setProductInfo = createAction<ProductInfoType>(
    'SET_PRODUCT_INFO',
    (id: string, name: string, link: string) => ({
        id,
        name,
        link,
    }),
);

/**
 * Set new product identification
 * @param {string} id - Product identification
 * @return {string} - Action data
 */
export const setProductId = createAction<string>('SET_PRODUCT_ID');

/**
 * Set new current step
 * @param {string} step - New step
 * @return {{step: string}} Action data
 */
export const setCurrentStep = createAction<{step: string}>(
    'TRY_ON_SET_CURRENT_STEP',
    (step: string) => ({
        step,
    }),
);

/**
 * State if editable block is touch
 * @param {boolean} isTouch - isTouch value
 * @return {{isTouch: boolean}} Action data
 */
export const setIsTouch = createAction<{isTouch: boolean}>('SET_IS_TOUCH', (isTouch: boolean) => ({
    isTouch,
}));

/**
 * Set PD
 * @param {number} pd - New PD value
 * @return {{pd: number}} - Action data
 */
export const setPd = createAction<{pd: number}>('SET_PD', (pd: number) => ({pd}));
